import React from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, Plus, AlertTriangle, Lightbulb, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import {
  lensDisplayName,
  lensObstacleQuestion,
  lensOpportunityQuestion,
} from '../../utils/lensDisplay';
import LensItemCard from './LensItemCard';
import PivotContextLine from './PivotContextLine';
import { getDeviceId } from '../../utils/deviceId';
import AIButton from '../AIButton';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  color: white;

  html[data-platform='tizen'] & {
    background: #12121a;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 16px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const TitleStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const LensTitle = styled.h1`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${props => props.$color || '#fff'};
`;

const AddButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.12);
  margin: 0 4px;
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  background: ${props => props.$color}18;
  border: 1px solid ${props => props.$color}44;
  border-radius: 8px;
  color: ${props => props.$color};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.$color}30;
    border-color: ${props => props.$color};
  }
`;

const AIBtn = styled(AIButton)`
  border-radius: 8px;
`;
AIBtn.defaultProps = { $iconOnly: true };

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px 32px;
`;

const PivotSection = styled.div`
  margin: 0 auto 28px;
  max-width: 720px;
`;

const PivotHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const PivotTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  flex: 1;
`;

const ItemCount = styled.span`
  font-size: 0.75rem;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyPivot = styled.div`
  padding: 16px;
  text-align: center;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.25);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 10px;
`;

const EmptyText = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.4);
  max-width: 480px;
  line-height: 1.5;
  margin: 8px auto 20px;
  text-align: center;
`;

const GuidingQuestions = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 32px 16px;
  flex-shrink: 0;
`;

const QuestionCard = styled.div`
  flex: 1;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${props => props.$color}0a;
  border: 1px solid ${props => props.$color}25;
  font-size: 0.82rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.6);

  strong {
    color: ${props => props.$color};
    font-weight: 600;
  }
`;

/**
 * LensDetailView - Pivot points with their obstacles/opportunities for a specific lens.
 * Maps to Figma Screens 4 (empty) and 6 (enriched).
 */
export default function LensDetailView({
  lens,
  pivotPointTitle = '',
  pivotPoints = [],
  items = [],
  enrichingItemIds = new Set(),
  pendingFormCheckIds = new Set(),
  dismissedFormCheckIds = new Set(),
  onDismissFormCheck,
  onBack,
  onAddItem,
  onAIGenerate,
  onItemClick,
  onItemEdit,
  onItemDelete,
  showDescription = true,
}) {
  const { t, language } = useTranslation();
  const color = lens?.color || '#2E7BC4';
  const obstacleQ = lensObstacleQuestion(lens, language);
  const opportunityQ = lensOpportunityQuestion(lens, language);

  // Group items by pivot point
  const itemsByPivot = {};
  items.forEach(item => {
    if (!itemsByPivot[item.pivot_point_id]) {
      itemsByPivot[item.pivot_point_id] = [];
    }
    itemsByPivot[item.pivot_point_id].push(item);
  });

  // Build render list: use provided pivotPoints, but also include any pivots
  // that have items but aren't in the provided list (handles stale selectedPivotId)
  const pivotLookup = new Set(pivotPoints.map(pp => pp.id));
  const extraPivotIds = Object.keys(itemsByPivot).filter(id => !pivotLookup.has(id));
  const renderPivots = [
    ...pivotPoints,
    ...extraPivotIds.map(id => ({ id, title: items.find(i => i.pivot_point_id === id)?.pivot_title || t('lenses.pivotLabel') })),
  ];

  return (
    <Container>
      <HeaderBar>
        <HeaderLeft>
          {onBack && (
            <BackButton onClick={onBack}>
              <ArrowLeft size={16} />
              {t('lenses.stepLenses')}
            </BackButton>
          )}
          <TitleStack>
            <LensTitle $color={color}>{lensDisplayName(lens, language) || t('lenses.lensFallback')}</LensTitle>
            <PivotContextLine
              title={pivotPointTitle}
              label={t('lenses.pivotLabel')}
              compact
            />
          </TitleStack>
        </HeaderLeft>
        <AddButtons>
          {(onAddItem || onAIGenerate) && (
            <>
              {onAddItem && (
                <AddBtn $color="#E94B6E" onClick={() => onAddItem('obstacle')}>
                  {t('lenses.obstacle')} <Plus size={13} />
                </AddBtn>
              )}
              {onAIGenerate && (
                <AIBtn onClick={() => onAIGenerate('obstacle')} title={t('lenses.aiObstacles')}>
                  <Sparkles size={14} />
                </AIBtn>
              )}
              <ButtonDivider />
              {onAddItem && (
                <AddBtn $color="#A6D64D" onClick={() => onAddItem('opportunity')}>
                  {t('lenses.opportunity')} <Plus size={13} />
                </AddBtn>
              )}
              {onAIGenerate && (
                <AIBtn onClick={() => onAIGenerate('opportunity')} title={t('lenses.aiOpportunities')}>
                  <Sparkles size={14} />
                </AIBtn>
              )}
            </>
          )}
        </AddButtons>
      </HeaderBar>

      {(obstacleQ || opportunityQ) && (
        <GuidingQuestions>
          {obstacleQ && (
            <QuestionCard $color="#E94B6E">
              <strong>{t('lenses.obstacles')}:</strong> {obstacleQ}
            </QuestionCard>
          )}
          {opportunityQ && (
            <QuestionCard $color="#A6D64D">
              <strong>{t('lenses.opportunities')}:</strong> {opportunityQ}
            </QuestionCard>
          )}
        </GuidingQuestions>
      )}

      <ScrollArea>
        {items.length === 0 && (
          <EmptyText>
            {t('lenses.emptyLensDetail', { lens: lensDisplayName(lens, language) || lens?.name })}
            {onAIGenerate && ` ${t('lenses.emptyLensDetailAi')}`}
          </EmptyText>
        )}

        {renderPivots.map(pp => {
          const ppItems = itemsByPivot[pp.id] || [];
          const obstacles = ppItems.filter(i => i.type === 'obstacle');
          const opportunities = ppItems.filter(i => i.type === 'opportunity');

          return (
            <PivotSection key={pp.id}>
              <PivotHeader>
                <PivotTitle>{pp.title}</PivotTitle>
                {obstacles.length > 0 && (
                  <ItemCount $color="#E94B6E">
                    <AlertTriangle size={12} />
                    {obstacles.length}
                  </ItemCount>
                )}
                {opportunities.length > 0 && (
                  <ItemCount $color="#A6D64D">
                    <Lightbulb size={12} />
                    {opportunities.length}
                  </ItemCount>
                )}
              </PivotHeader>

              {ppItems.length === 0 ? (
                <EmptyPivot>
                  {t('lenses.emptyNoItemsYet')}
                </EmptyPivot>
              ) : (
                <ItemList>
                  {ppItems.map(item => (
                    <LensItemCard
                      key={item.id}
                      item={item}
                      type={item.type}
                      enriching={enrichingItemIds.has(item.id)}
                      formCheckPending={pendingFormCheckIds.has(item.id)}
                      formCheckDismissed={dismissedFormCheckIds.has(item.id)}
                      onDismissFormCheck={() => onDismissFormCheck?.(item.id)}
                      onClick={() => onItemClick?.(item)}
                      onEdit={onItemEdit ? () => onItemEdit(item) : undefined}
                      onDelete={onItemDelete ? () => onItemDelete(item) : undefined}
                      showDescription={showDescription}
                      currentDeviceId={getDeviceId()}
                    />
                  ))}
                </ItemList>
              )}
            </PivotSection>
          );
        })}
      </ScrollArea>
    </Container>
  );
}
